import React, { useEffect, useRef, useState } from "react";
import useOrders from "../../hooks/useOrders";
import CommonTable from "../../components/CommonTable";
import InputBox from "../../components/InputBox";
import { useSearch } from "../../components/SearchContext";
import iconMap from "../../../../assets/icons/Icons";
import { useNavigate, useSearchParams } from "react-router-dom";
import LoadingScreen from "../../../../shared/components/LoadingScreen";
import { TablePagination } from "@mui/material";
import useExportOrder from "../../hooks/useExportOrder";
import { notifyWarning } from "src/assets/toastService";
import { DEFAULT_ROWS_PER_PAGE_OPTIONS } from "../../hooks/tableConfig";
import { statusColors } from "./OrderSidePanal";
import { Button } from "../../components/Button";
import CustomDropdownSelect from "../../components/DropdownSelect";
import { useUser } from "../../components/UserContext";
import { formatDate } from "src/business-types/utils/dateTimeUtils";
import { STATUS_OPTIONS } from "src/business-types/car-wash/modules/orders/carOrderDetails/components/orderSummary/OrderSummaryConstants";
import { reactAppVersion } from "src/features/auth/axiosConfig";

// Helper function to format bill number as 4 digits
export const formatBillNumber = (value: number | undefined) => {
  if (!value && value !== 0) return "N/A";
  return value.toString().padStart(4, "0");
};

function OrderTable() {
  const MIN_FROM_DATE = "2025-01-01";
  const now = new Date();
  const today = now.getFullYear() + "-" + String(now.getMonth() + 1).padStart(2, "0") + "-" + String(now.getDate()).padStart(2, "0");

  const [pages, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [orderby, setOrderBy] = useState("");
  const [field, setField] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const { userConfigration } = useUser();

  // Get configuration settings
  const toBoolean = (value: any) => value === true || value === "true";
  const billNoEnabled = toBoolean(userConfigration?.billNoEnabled);
  const orderIdEnabled = toBoolean(userConfigration?.orderIdEnabled);

  const [formData, setFormData] = useState({
    from: today,
    to: today,
  });

  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const DownArrow = iconMap["down"];

  const navigation = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { searchTerm, setSearchTerm } = useSearch();
  const { handleExport } = useExportOrder();

  /* =======================
     URL → STATE (ON LOAD + CHANGE)
     ======================= */
  useEffect(() => {
    const pageParam = Number(searchParams.get("page")) || 1;
    const limitParam = Number(searchParams.get("limit")) || 10;

    setPage(pageParam - 1);
    setRowsPerPage(limitParam);
    setSearchTerm(searchParams.get("search") || "");
    setStatusFilter(searchParams.get("status") || "");
    setFormData({
      from: searchParams.get("from") || today,
      to: searchParams.get("to") || today,
    });
  }, [searchParams]);

  useEffect(() => {
    setRowsPerPage(userConfigration?.tableDefaultSize || 10);
  }, [userConfigration]);

  /**API CALL */
  const { orderData, loadingOrder, orderTable } = useOrders(
    "",
    "",
    pages + 1,
    rowsPerPage,
    formData.from,
    formData.to,
    searchTerm,
    "",
    field,
    orderby,
    statusFilter,
  );

  /** PAGINATION → URL */
  const handleChangePage = (_: any, newPage: number) => {
    setSearchParams({
      page: (newPage + 1).toString(),
      limit: rowsPerPage.toString(),
      search: searchTerm,
      from: formData.from,
      to: formData.to,
      status: statusFilter,
    });
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newLimit = parseInt(event.target.value, 10);

    setSearchParams({
      page: "1",
      limit: newLimit.toString(),
      search: searchTerm,
      from: formData.from,
      to: formData.to,
      status: statusFilter,
    });
  };

  /* =======================
     DATE CHANGE → URL
     ======================= */
  const handleDateChange = (name: "from" | "to", value: string) => {
    const updated = { ...formData, [name]: value };
    setFormData(updated);

    setSearchParams({
      page: "1",
      limit: rowsPerPage.toString(),
      search: searchTerm,
      from: updated.from,
      to: updated.to,
    });
  };

  /* =======================
     DYNAMIC HEADER CELLS BASED ON CONFIGURATION
     ======================= */
  const getDynamicHeaders = () => {
    // Start with always visible columns
    const headers: any[] = [
      { id: "S.No", label: "S.No", numeric: false, sorting: false },
      { id: "Orderdate", label: "Date", numeric: false },
    ];

    // Add Order ID column if enabled
    if (orderIdEnabled) {
      headers.push({ id: "Orderid", label: "Order ID", numeric: false });
    }

    // Add Bill Number column if enabled
    if (billNoEnabled) {
      headers.push({ id: "billNumber", label: "Bill No.", numeric: false });
    }

    // Add remaining columns
    headers.push(
      { id: "CustomerName", label: "Customer Name", numeric: false },
      { id: "CustomerPhoneNo", label: "Phone Number", numeric: false },
      { id: "OrderStatus", label: "Order Status", numeric: false }
    );

    return headers;
  };

  /* =======================
     TABLE DATA - CONDITIONALLY ADD FIELDS
     ======================= */
  const OrderTableData = orderData.map((item, index) => {
    const order = item?.orders?.[0];

    // ✅ Customer Name
    const name = [
      item?.customerDetails?.firstName,
      item?.customerDetails?.middleName,
      item?.customerDetails?.lastName
    ]
      .filter(Boolean)
      .join(" ");

    // ✅ Phone
    const phone =
      item?.customerDetails?.phones?.[0]?.phone || "";

    // ✅ Date
    const orderDate = order?.createdOn.$date;

    const row: any = {
      "S.No": pages * rowsPerPage + index + 1,

      id: item?.orderId,

      Orderdate: formatDate(orderDate),

      CustomerName: name,

      CustomerPhoneNo: phone,

      OrderStatus: (
        <span
          className={`px-2 py-1 rounded font-semibold text-sm ${statusColors[order?.status] || "bg-gray-200 text-gray-700"
            }`}
        >
          {order?.status || ""}
        </span>
      ),
    };

    // ✅ Order ID (optional toggle)
    if (orderIdEnabled) {
      row.Orderid = item?.orderId;
    }

    // ✅ Bill Number (optional toggle)
    if (billNoEnabled) {
      row.billNumber = formatBillNumber(order?.billNumber);
    }

    return row;
  });

  const dataisEmpty = OrderTableData.length === 0;

  /* =======================
     OUTSIDE CLICK
     ======================= */
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    }

    if (showDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showDropdown]);

  return (
    <div className='w-full h-full flex flex-col items-center overflow-y-auto p-1'>
      {(loadingOrder || !orderData) && <LoadingScreen />}

      {/* FILTERS */}
      <div className='w-full flex justify-between px-10'>
        <div className='flex gap-2'>
          <div>
            <p className='text-sm font-medium mb-1'>From</p>
            <InputBox
              type='date'
              value={formData.from}
              min={MIN_FROM_DATE}
              max={formData.to || today}
              onChange={(e) => handleDateChange("from", e.target.value)}
              className='w-44'
            />
          </div>

          <div>
            <p className='text-sm font-medium mb-1'>To</p>
            <InputBox
              type='date'
              value={formData.to}
              min={formData.from || MIN_FROM_DATE}
              max={today}
              onChange={(e) => handleDateChange("to", e.target.value)}
              className='w-44'
            />
          </div>
        </div>

        <div className='flex relative gap-2 items-center' ref={dropdownRef}>
          <div className='w-40'>
            <p>Status Filter</p>
            <CustomDropdownSelect
              name='statusSelected'
              value={statusFilter}
              titleofSelect='All Status'
              options={STATUS_OPTIONS.map((o) => ({
                _id: o,
                CategoryName: o,
              }))}
              onChange={(e) => setStatusFilter(e)}
            />
          </div>

          <Button
            variant='ThemeBase'
            onClick={() => (dataisEmpty ? notifyWarning("Data not available") : setShowDropdown(!showDropdown))}
            icon={DownArrow}
            iconPosition='right'
            label='Export'
          />

          {showDropdown && (
            <div className='absolute top-full mt-1 right-0 bg-white border rounded shadow-lg z-10 w-32'>
              <button
                className='w-full px-4 py-2 text-left hover:bg-gray-100'
                onClick={() => {
                  handleExport({
                    type: "csv",
                    search: searchTerm,
                    pageNumber: pages + 1,
                    pageSize: rowsPerPage,
                    startDate: formData.from,
                    endDate: formData.to,
                    OrderStatus: statusFilter,
                  });
                  setShowDropdown(false);
                }}
              >
                CSV
              </button>

              <button
                className='w-full px-4 py-2 text-left hover:bg-gray-100'
                onClick={() => {
                  handleExport({
                    type: "pdf",
                    search: searchTerm,
                    pageNumber: pages + 1,
                    pageSize: rowsPerPage,
                    startDate: formData.from,
                    endDate: formData.to,
                    OrderStatus: statusFilter,
                  });
                  setShowDropdown(false);
                }}
              >
                PDF
              </button>
            </div>
          )}
        </div>
      </div>

      {/* TABLE */}
      <CommonTable
        data={OrderTableData}
        headCells={getDynamicHeaders()}
        pageSize={rowsPerPage}
        title='Order List'
        selectable={false}
        pagenation={false}
        shortByTable={false}
        onRowClick={(orderid) => navigation(`/grocery/${reactAppVersion}/orders/${orderid}`)}
        OrderType={(data) => {
          setField(data.field);
          setOrderBy(data.orderby);
        }}
      />

      {/* PAGINATION */}
      <div className='w-[95%] bg-white border -mt-2 rounded-b-md mb-4'>
        <TablePagination
          rowsPerPageOptions={DEFAULT_ROWS_PER_PAGE_OPTIONS}
          component='div'
          count={orderTable?.totalRecords || 0}
          rowsPerPage={rowsPerPage}
          page={pages}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </div>
    </div>
  );
}

export default OrderTable;